package org.multiplatform.kotfolio.pages.compose

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em

@Page("/compose/bottom_sheet")
@Composable
fun BottomSheetComposePage() {
    Column(
        horizontalAlignment = Alignment.Start,
        modifier = Modifier.fillMaxSize().padding(leftRight = 0.1.em),
    ) {
        SpanText(
            text = "FlexibleBottomSheet",
            modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                .fontWeight(FontWeight.Bold)
        )

        //https://doveletter.dev/preview/articles/flexible-bottomsheet-google-maps#putting-it-all-together
        KotlinCode(
            code = """
                    @Composable
                    fun PlaceDetailScreen() {
                        var targetValue by remember {
                            mutableStateOf(FlexibleSheetValue.SlightlyExpanded)
                        }

                        Box(modifier = Modifier.fillMaxSize()) {
                            // Map content behind the sheet
                            MapView(modifier = Modifier.fillMaxSize())

                            // Non-modal bottom sheet with three states
                            FlexibleBottomSheet(
                                onDismissRequest = { /* handle dismiss */ },
                                sheetState = rememberFlexibleBottomSheetState(
                                    flexibleSheetSize = FlexibleSheetSize(
                                        fullyExpanded = 0.9f,
                                        intermediatelyExpanded = 0.5f,
                                        slightlyExpanded = 0.15f,
                                    ),
                                    isModal = false,
                                    skipSlightlyExpanded = false,
                                    skipHiddenState = true,
                                ),
                                onTargetChanges = { targetValue = it },
                                containerColor = Color.White,
                                shape = RoundedCornerShape(topStart = 16.dp, topEnd = 16.dp),
                            ) {
                                PlaceContent(targetValue = targetValue)
                            }
                        }
                    }

                    @Composable
                    private fun ColumnScope.PlaceContent(targetValue: FlexibleSheetValue) {
                        // Header: always visible
                        Row(
                            modifier = Modifier.fillMaxWidth().padding(16.dp),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Column(modifier = Modifier.weight(1f)) {
                                Text(
                                    text = "Central Park",
                                    fontSize = 18.sp,
                                    fontWeight = FontWeight.Bold,
                                    maxLines = 1,
                                )
                                Text(
                                    text = "4.8 stars - Park",
                                    fontSize = 14.sp,
                                    color = Color.Gray,
                                )
                            }
                        }

                        // Details: visible when intermediately or fully expanded
                        if (targetValue != FlexibleSheetValue.SlightlyExpanded) {
                            ActionButtons()
                            Spacer(modifier = Modifier.height(8.dp))
                        }

                        // Full content: scrollable list when fully expanded
                        if (targetValue == FlexibleSheetValue.FullyExpanded) {
                            LazyColumn(modifier = Modifier.fillMaxSize()) {
                                item { PlacePhotos() }
                                item { PlaceReviews() }
                                item { PlaceInfo() }
                            }
                        }
                    }
                    """.trimIndent()
        )

        SpanText(
            text = "ModalBottomSheet",
            modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                .fontWeight(FontWeight.Bold)
        )

        KotlinCode(
            code = """
                    //ModalBottomSheet outside click dismiss
                    
                    ModalBottomSheet(
                        onDismissRequest = onDismiss,
                        properties = ModalBottomSheetProperties(
                            shouldDismissonClickoutside = false
                        ) 
                    ){ }
                    """.trimIndent()
        )

        KotlinCode(
            code = """
                    //Background overlay
                    
                    @Composable
                    fun BlurredScrimBottomSheet(
                        onDismiss: () -> Unit,
                        content: @Composable ColumnScope.() -> Unit
                    ) {
                        ModalBottomSheet(
                            onDismissRequest = onDismiss,
                            scrimColor = MaterialTheme.colorScheme.surface.copy(alpha = 0.5f)
                        ) {
                            Column(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(16.dp),
                                content = content
                            )
                        }
                    }
                    """.trimIndent()
        )

        KotlinCode(
            code = """
                    //Handling Keyboard Interactions
                    
                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun KeyboardAwareBottomSheet(
                        onDismiss: () -> Unit,
                        content: @Composable ColumnScope.() -> Unit
                    ) {
                        val keyboardController = LocalSoftwareKeyboardController.current
                        
                        ModalBottomSheet(
                            onDismissRequest = {
                                keyboardController?.hide()
                                onDismiss()
                            },
                            windowInsets = WindowInsets.ime
                        ) {
                            Column(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .imePadding()
                                    .padding(16.dp),
                                content = content
                            )
                        }
                    }
                    """.trimIndent()
        )

        KotlinCode(
            code = """
                    //Animated ModalBottomSheet
                    
                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun AnimatedBottomSheet(
                        onDismiss: () -> Unit,
                        content: @Composable ColumnScope.() -> Unit
                    ) {
                        val sheetState = rememberModalBottomSheetState()
                        var contentAlpha by remember { mutableFloatStateOf(0f) }
                        
                        LaunchedEffect(sheetState.isVisible) {
                            if (sheetState.isVisible) {
                                animate(
                                    initialValue = 0f,
                                    targetValue = 1f,
                                    animationSpec = tween(durationMillis = 300)
                                ) { value, _ ->
                                    contentAlpha = value
                                }
                            }
                        }
                        
                        ModalBottomSheet(
                            onDismissRequest = onDismiss,
                            sheetState = sheetState
                        ) {
                            Column(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .alpha(contentAlpha)
                                    .padding(16.dp),
                                content = content
                            )
                        }
                    }
                    """.trimIndent()
        )

        KotlinCode(
            code = $$$"""
                    //Filter Sheet
                    
                    @Composable
                    fun CustomDragHandle(
                        modifier: Modifier = Modifier,
                        color: Color = MaterialTheme.colorScheme.onSurfaceVariant
                    ) {
                        Box(
                            modifier = modifier
                                .fillMaxWidth()
                                .padding(vertical = 12.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            Surface(
                                modifier = Modifier
                                    .width(48.dp)
                                    .height(4.dp),
                                shape = RoundedCornerShape(2.dp),
                                color = color.copy(alpha = 0.4f)
                            ) {}
                        }
                    }
                    
                    data class FilterState(
                        val category: String? = null,
                        val priceRange: ClosedFloatingPointRange<Float> = 0f..1000f,
                        val sortBy: SortOption = SortOption.RELEVANCE,
                        val inStock: Boolean = false
                    )

                    enum class SortOption {
                        RELEVANCE, PRICE_LOW, PRICE_HIGH, NEWEST
                    }

                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun FilterBottomSheet(
                        currentFilters: FilterState,
                        categories: List<String>,
                        onApplyFilters: (FilterState) -> Unit,
                        onDismiss: () -> Unit
                    ) {
                        var filters by remember { mutableStateOf(currentFilters) }
                        val sheetState = rememberModalBottomSheetState()
                        
                        ModalBottomSheet(
                            onDismissRequest = onDismiss,
                            sheetState = sheetState,
                            dragHandle = { CustomDragHandle() }
                        ) {
                            Column(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(16.dp)
                                    .verticalScroll(rememberScrollState())
                            ) {
                                Text(
                                    "Filters",
                                    style = MaterialTheme.typography.headlineSmall,
                                    fontWeight = FontWeight.Bold
                                )
                                
                                Spacer(modifier = Modifier.height(24.dp))
                                
                                // Category Section
                                Text(
                                    "Category",
                                    style = MaterialTheme.typography.titleMedium,
                                    fontWeight = FontWeight.Medium
                                )
                                Spacer(modifier = Modifier.height(8.dp))
                                
                                FlowRow(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                                ) {
                                    categories.forEach { category ->
                                        FilterChip(
                                            selected = filters.category == category,
                                            onClick = { 
                                                filters = filters.copy(
                                                    category = if (filters.category == category) null else category
                                                )
                                            },
                                            label = { Text(category) }
                                        )
                                    }
                                }
                                
                                Spacer(modifier = Modifier.height(24.dp))
                                
                                // Price Range Section
                                Text(
                                    "Price Range",
                                    style = MaterialTheme.typography.titleMedium,
                                    fontWeight = FontWeight.Medium
                                )
                                Spacer(modifier = Modifier.height(8.dp))
                                
                                RangeSlider(
                                    value = filters.priceRange,
                                    onValueChange = { filters = filters.copy(priceRange = it) },
                                    valueRange = 0f..1000f,
                                    steps = 19
                                )
                                
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.SpaceBetween
                                ) {
                                    Text(
                                        "${filters.priceRange.start.toInt()}",
                                        style = MaterialTheme.typography.bodyMedium
                                    )
                                    Text(
                                        "$${filters.priceRange.endInclusive.toInt()}",
                                        style = MaterialTheme.typography.bodyMedium
                                    )
                                }
                                
                                Spacer(modifier = Modifier.height(24.dp))
                                
                                // Sort By Section
                                Text(
                                    "Sort By",
                                    style = MaterialTheme.typography.titleMedium,
                                    fontWeight = FontWeight.Medium
                                )
                                Spacer(modifier = Modifier.height(8.dp))
                                
                                SortOption.entries.forEach { option ->
                                    Row(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .clickable { filters = filters.copy(sortBy = option) }
                                            .padding(vertical = 12.dp),
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        RadioButton(
                                            selected = filters.sortBy == option,
                                            onClick = { filters = filters.copy(sortBy = option) }
                                        )
                                        Spacer(modifier = Modifier.width(12.dp))
                                        Text(
                                            option.name.lowercase().replace('_', ' ')
                                                .replaceFirstChar { it.uppercase() },
                                            style = MaterialTheme.typography.bodyLarge
                                        )
                                    }
                                }
                                
                                Spacer(modifier = Modifier.height(16.dp))
                                
                                // In Stock Toggle
                                Row(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(vertical = 8.dp),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Text(
                                        "In Stock Only",
                                        style = MaterialTheme.typography.bodyLarge
                                    )
                                    Switch(
                                        checked = filters.inStock,
                                        onCheckedChange = { filters = filters.copy(inStock = it) }
                                    )
                                }
                                
                                Spacer(modifier = Modifier.height(24.dp))
                                
                                // Action Buttons
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                                ) {
                                    OutlinedButton(
                                        onClick = {
                                            filters = FilterState()
                                        },
                                        modifier = Modifier.weight(1f)
                                    ) {
                                        Text("Clear All")
                                    }
                                    
                                    Button(
                                        onClick = {
                                            onApplyFilters(filters)
                                            onDismiss()
                                        },
                                        modifier = Modifier.weight(1f)
                                    ) {
                                        Text("Apply Filters")
                                    }
                                }
                                
                                Spacer(modifier = Modifier.height(16.dp))
                            }
                        }
                    }
                    """.trimIndent()
        )

        SpanText(
            text = "Flexible BottomSheet",
            modifier = Modifier
                .fontSize(FontSize.Medium)
                .fontWeight(FontWeight.Bold)
        )

        KotlinCode(
            code = """
                    val scope = rememberCoroutineScope()
                    val sheetState = rememberFlexibleBottomSheetState(
                      flexibleSheetSize = FlexibleSheetSize(fullyExpanded = 0.9f),
                      isModal = true,
                      skipSlightlyExpanded = false,
                    )

                    FlexibleBottomSheet(
                      sheetState = sheetState,
                      containerColor = Color.Black,
                      onDismissRequest = onDismissRequest
                    ) {
                      Button(
                        modifier = Modifier.align(Alignment.CenterHorizontally),
                        onClick = {
                          scope.launch {
                            when (sheetState.swipeableState.currentValue) {
                              FlexibleSheetValue.SlightlyExpanded -> sheetState.intermediatelyExpand()
                              FlexibleSheetValue.IntermediatelyExpanded -> sheetState.fullyExpand()
                              else -> sheetState.hide()
                            }
                          }
                        },
                      ) {
                        Text(text = "Expand Or Hide")
                      }
                    }
                    """.trimIndent()
        )

        //https://github.com/Cairo-Squad/EvolveFit.git
        KotlinCode(
            code = $$"""
                    //DatePicker Vertical 
                    
                    @Composable
                    fun DateBottomSheet(
                        maxDate: String,
                        dateOfBirth: String,
                        isDatePickerBottomSheetOpen: Boolean=true,
                        onDatePickerDismiss: () -> Unit,
                        onDateChange: (String) -> Unit,
                        modifier: Modifier = Modifier
                    ) {
                        val date = dateOfBirth.ifBlank { maxDate }
                        val day = getDayFromDate(date)
                        val dayPagerState = rememberPagerState(
                            initialPage = day.toInt() - 1,
                            pageCount = {
                                getNumberOfDaysInMonth(
                                    year = getYearFromDate(date).toInt(),
                                    month = getMonthAsInt(getMonthFromDate(date))
                                )
                            }
                        )

                        val monthPagerState = rememberPagerState(
                            initialPage = getMonthAsInt(getMonthFromDate(date)) - 1,
                            pageCount = { 12 }
                        )

                        val currentYear = getYearFromDate(date).toInt()
                        val maxYear = getYearFromDate(maxDate).toInt()

                        val yearPagerState = rememberPagerState(
                            initialPage = currentYear - FIRST_YEAR,
                            pageCount = { maxYear - FIRST_YEAR + 1 }
                        )

                        BottomSheet(
                            isVisible = isDatePickerBottomSheetOpen,
                            onDismiss = onDatePickerDismiss,
                            modifier = modifier
                                .fillMaxWidth()
                        ) {
                            DateBottomSheetContent(
                                dayPagerState = dayPagerState,
                                monthPagerState = monthPagerState,
                                yearPagerState = yearPagerState,
                                onDateChange = {
                                    onDateChange(
                                        getDateFromYearMonthDay(
                                            year = yearPagerState.currentPage + FIRST_YEAR,
                                            month = monthPagerState.currentPage + 1,
                                            day = dayPagerState.currentPage + 1
                                        )
                                    )
                                },
                                onDatePickerDismiss = onDatePickerDismiss,
                                modifier = Modifier
                                    .padding(16.dp)
                            )
                        }
                    }

                    @Composable
                    fun DateBottomSheetContent(
                        dayPagerState: PagerState,
                        monthPagerState: PagerState,
                        yearPagerState: PagerState,
                        onDateChange: () -> Unit,
                        onDatePickerDismiss: () -> Unit,
                        modifier: Modifier = Modifier
                    ) {
                        Column(
                            modifier = modifier
                                .fillMaxWidth()
                        ) {
                            Text(
                                text = "Birthday", // TODO
                                style = Theme.textStyle.label.mediumMedium16,
                                color = Theme.color.surfaces.onSurface,
                                modifier = Modifier
                                    .padding(bottom = 4.dp)
                            )

                            Text(
                                text = "Please select your birthday", // TODO
                                style = Theme.textStyle.label.mediumMedium12,
                                color = Theme.color.surfaces.onSurfaceVariant,
                                modifier = Modifier
                                    .padding(bottom = 16.dp)
                            )

                            DatePicker(
                                dayPagerState = dayPagerState,
                                monthPagerState = monthPagerState,
                                yearPagerState = yearPagerState,
                                modifier = Modifier
                                    .padding(bottom = 40.dp)
                                    .fillMaxWidth()
                            )

                            PrimaryButton(
                                text = "Save", // TODO
                                onClick = {
                                    onDateChange()
                                    onDatePickerDismiss()
                                }
                            )
                        }
                    }

                    @Preview
                    @Composable
                    fun PreviewDateBottomSheet() {
                        AppTheme {
                            DateBottomSheet(
                                maxDate = "2023-08-06",
                                dateOfBirth = "2023-08-06",
                                onDateChange = {},
                                isDatePickerBottomSheetOpen = true,
                                onDatePickerDismiss = {},
                                modifier = Modifier
                                    .padding(16.dp)
                            )
                        }
                    }
                    
                    @Composable
                    fun DatePicker(
                        dayPagerState: PagerState,
                        monthPagerState: PagerState,
                        yearPagerState: PagerState,
                        modifier: Modifier = Modifier
                    ) {

                        val days = remember {
                            (1..getNumberOfDaysInMonth(
                                year = yearPagerState.currentPage + FIRST_YEAR,
                                month = monthPagerState.currentPage + 1
                            )).map { number ->
                                if (number < 10) "0$number" else number.toString()
                            }
                        }

                        Box(
                            modifier = modifier
                                .fillMaxWidth()
                                .height(252.dp),
                        ) {
                            ChoiceIndicator(
                                modifier = Modifier
                                    .align(Alignment.Center)
                            )

                            Row(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .padding(horizontal = 32.dp),
                            ) {
                                VerticalPicker(
                                    state = dayPagerState,
                                    options = days,
                                    modifier = Modifier
                                        .fillMaxHeight()
                                        .weight(1f)
                                )

                                VerticalPicker(
                                    state = monthPagerState,
                                    options = (1..12).map { getMonthName(it) },
                                    modifier = Modifier
                                        .fillMaxHeight()
                                        .weight(1f)
                                )

                                VerticalPicker(
                                    state = yearPagerState,
                                    options = (MIN_YEAR..MAX_YEAR).map { it.toString() },
                                    modifier = Modifier
                                        .fillMaxHeight()
                                        .weight(1f)
                                )
                            }
                        }
                    }

                    @Composable
                    private fun ChoiceIndicator(
                        modifier: Modifier = Modifier
                    ) {
                        Box(
                            modifier = modifier
                                .clip(RoundedCornerShape(8.dp))
                                .fillMaxWidth()
                                .height(40.dp)
                                .background(Theme.color.surfaces.surfaceContainer)
                        )
                    }

                    @Composable
                    private fun VerticalPicker(
                        state: PagerState,
                        options: List<String>,
                        modifier: Modifier = Modifier
                    ) {
                        VerticalPager(
                            state = state,
                            modifier = modifier,
                            horizontalAlignment = Alignment.CenterHorizontally,
                            pageSpacing = 4.dp,
                            contentPadding = PaddingValues(vertical = 106.dp),
                            pageSize = PageSize.Fixed(40.dp),
                        ) { page ->
                            val animatedContentColor by animateColorAsState(
                                targetValue = if (page == state.currentPage) Theme.color.surfaces.onSurfaceContainer else Theme.color.surfaces.outlineVariant
                            )

                            Box(
                                modifier = Modifier
                                    .fillMaxHeight(),
                                contentAlignment = Alignment.Center
                            ) {
                                Text(
                                    text = options.getOrNull(page) ?: "",
                                    style = Theme.textStyle.body.mediumMedium14,
                                    color = animatedContentColor,
                                    modifier = Modifier
                                )
                            }
                        }
                    }

                    private const val MIN_YEAR = 1900
                    private const val MAX_YEAR = 2100
                    private const val INITIAL_DATE = "2025-08-06"

                    @Preview
                    @Composable
                    fun PreviewDatePicker() {

                        val month by remember { mutableStateOf(getMonthFromDate(INITIAL_DATE)) }
                        val year by remember { mutableStateOf(getYearFromDate(INITIAL_DATE)) }

                        val dayPagerState =
                            rememberPagerState { getNumberOfDaysInMonth(year.toInt(), getMonthAsInt(month)) }
                        val monthPagerState = rememberPagerState { 12 }
                        val yearPagerState = rememberPagerState { MAX_YEAR - MIN_YEAR + 1 }

                        AppTheme(isDarkTheme = true) {
                            DatePicker(
                                dayPagerState = dayPagerState,
                                monthPagerState = monthPagerState,
                                yearPagerState = yearPagerState,
                                modifier = Modifier
                                    .background(Theme.color.surfaces.surface)
                                    .padding(horizontal = 16.dp)
                            )
                        }
                    }
                    """.trimIndent()
        )
    }
}
//https://hrach.dev/posts/nav3-modalbottomsheet
//https://hrach.dev/posts/nav3-draggable-sheet
//https://proandroiddev.com/why-your-compose-multiplatform-app-still-needs-native-code-a7e56bffeaea
//https://doveletter.dev/preview/articles/flexible-bottomsheet-google-maps#putting-it-all-together